import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { isUserAdmin } from '../helpers/adminAuth';
import { ReportSnapshot, ReportRow } from '../services/reportsService';
import reportsService from '../services/reportsService';
import LifecycleBadge from '../components/LifecycleBadge/LifecycleBadge';
import { exportSnapshotToCsv } from '../utils/snapshotCsvExport';
import { parseReportDate } from '../utils/snapshotDate';
import './SnapshotReportPage.css';

/**
 * SnapshotReportPage - Displays a single snapshot report with filtering, pagination, and export
 * Similar to AdminReportsPage but for viewing historical snapshot data
 */
export const SnapshotReportPage: React.FC = () => {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const navigate = useNavigate();
  const { authState } = useOktaAuth();

  // State - Snapshot Data
  const [snapshot, setSnapshot] = useState<ReportSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State - Filters
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    enrolled: '',
    lob: '',
    product: '',
    area: '',
    lifecycleStage: '',
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // State - Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 25;

  // State - UI
  const [exporting, setExporting] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());

  // Fetch snapshot data
  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!authState?.isAuthenticated || !snapshotId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await reportsService.getSnapshotById(authState, snapshotId);
        setSnapshot(data);
      } catch (err) {
        console.error('Failed to fetch snapshot:', err);
        setError('Failed to load snapshot report. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshot();
  }, [authState, snapshotId]);

  // Verify admin access
  if (authState?.isPending) {
    return (
      <div className="snapshot-report-page">
        <div className="loading-message">Verifying permissions...</div>
      </div>
    );
  }

  if (!isUserAdmin(authState)) {
    return (
      <div className="snapshot-report-page">
        <div className="alert alert-error">
          <div>
            <h4>Access Denied</h4>
            <p>You do not have permission to view this report. Only admins can access this section.</p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Filter report data — for parent rows, filter child versions individually so the
  // expanded table only shows matching children (mirrors how the backend filters live data)
  const getFilteredData = (): ReportRow[] => {
    if (!snapshot) return [];

    const hasChildFilters = !!(filters.enrolled || filters.status || filters.lifecycleStage);

    return snapshot.reportData
      .map((row) => {
        // Filter child versions against all active child-level filters
        if (hasChildFilters && row.childVersions && row.childVersions.length > 0) {
          const filteredChildren = row.childVersions.filter((v: any) => {
            if (filters.enrolled && (v.isEnrolled ? 'true' : 'false') !== filters.enrolled) return false;
            if (filters.status && v.status !== filters.status) return false;
            if (filters.lifecycleStage && v.lifecycleStage?.toLowerCase() !== filters.lifecycleStage.toLowerCase()) return false;
            return true;
          });
          return { ...row, childVersions: filteredChildren };
        }
        return row;
      })
      .filter((row) => {
        if (search && !row.fullApiName.toLowerCase().includes(search.toLowerCase())) return false;
        if (filters.lob && row.lineOfBusiness !== filters.lob) return false;
        if (filters.product && row.product !== filters.product) return false;
        if (filters.area && row.area !== filters.area) return false;

        if (row.childVersions !== undefined) {
          // Versioned parent: hide if all children were filtered out
          if (hasChildFilters && row.childVersions.length === 0) return false;
        } else {
          // Standalone API: apply filters directly
          if (filters.enrolled && (row.isEnrolled ? 'true' : 'false') !== filters.enrolled) return false;
          if (filters.status && row.status !== filters.status) return false;
          if (filters.lifecycleStage && row.lifecycleStage?.toLowerCase() !== filters.lifecycleStage.toLowerCase()) return false;
        }

        return true;
      });
  };

  // Pagination
  const filteredData = getFilteredData();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredData.slice(startIndex, endIndex);

  // Handlers
  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (filters.status) count++;
    if (filters.enrolled) count++;
    if (filters.lob) count++;
    if (filters.product) count++;
    if (filters.area) count++;
    if (filters.lifecycleStage) count++;
    return count;
  };

  const formatStatus = (s: string) => {
    if (s === 'compliant') return 'Compliant';
    if (s === 'partially-compliant') return 'Partially Compliant';
    if (s === 'non-compliant') return 'Non-Compliant';
    if (s === 'not-analyzed') return 'Not Analyzed';
    return s;
  };

  const removeFilter = (filterType: string) => {
    setPage(1);
    if (filterType === 'search') { setSearch(''); setTempFilters(prev => ({...prev, search: ''})); }
    if (filterType === 'status') { setFilters(prev => ({...prev, status: ''})); setTempFilters(prev => ({...prev, status: ''})); }
    if (filterType === 'enrolled') { setFilters(prev => ({...prev, enrolled: ''})); setTempFilters(prev => ({...prev, enrolled: ''})); }
    if (filterType === 'lob') { setFilters(prev => ({...prev, lob: ''})); setTempFilters(prev => ({...prev, lob: ''})); }
    if (filterType === 'product') { setFilters(prev => ({...prev, product: ''})); setTempFilters(prev => ({...prev, product: ''})); }
    if (filterType === 'area') { setFilters(prev => ({...prev, area: ''})); setTempFilters(prev => ({...prev, area: ''})); }
    if (filterType === 'lifecycleStage') { setFilters(prev => ({...prev, lifecycleStage: ''})); setTempFilters(prev => ({...prev, lifecycleStage: ''})); }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({
      search,
      status: filters.status,
      enrolled: filters.enrolled,
      lob: filters.lob,
      product: filters.product,
      area: filters.area,
      lifecycleStage: filters.lifecycleStage,
    });
    setFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setSearch(tempFilters.search);
    setFilters({
      search: tempFilters.search,
      status: tempFilters.status,
      enrolled: tempFilters.enrolled,
      lob: tempFilters.lob,
      product: tempFilters.product,
      area: tempFilters.area,
      lifecycleStage: tempFilters.lifecycleStage,
    });
    setPage(1);
    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      search: '',
      status: '',
      enrolled: '',
      lob: '',
      product: '',
      area: '',
      lifecycleStage: '',
    };
    setSearch('');
    setFilters(emptyFilters);
    setPage(1);
  };

  const handleResetFiltersModal = () => {
    setTempFilters({
      search: '',
      status: '',
      enrolled: '',
      lob: '',
      product: '',
      area: '',
      lifecycleStage: '',
    });
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleExportCsv = async () => {
    if (!snapshot) return;
    try {
      setExporting(true);
      exportSnapshotToCsv(snapshot, filteredData);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleBackClick = () => {
    navigate('/admin/reports');
  };

  const toggleParentExpansion = (apiId: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(apiId)) {
        next.delete(apiId);
        // Also collapse all children's findings when parent collapses
        setExpandedFindings(new Set());
      } else {
        next.add(apiId);
      }
      return next;
    });
  };

  const toggleFindingsExpansion = (versionId: string) => {
    setExpandedFindings(prev => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string): string => {
    const date = parseReportDate(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="snapshot-report-page">
        <div className="loading-message">Loading snapshot report...</div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="snapshot-report-page">
        <div className="error-message">{error || 'Snapshot not found'}</div>
        <button className="btn btn-primary" onClick={handleBackClick}>
          ← Back to Snapshots
        </button>
      </div>
    );
  }

  // Get unique values for dropdowns — include child version fields so filters surface all options
  const uniqueStatuses = Array.from(new Set([
    ...snapshot.reportData.map((r) => r.status),
    ...snapshot.reportData.flatMap((r) => r.childVersions?.map((v: any) => v.status).filter(Boolean) || []),
  ])).sort();
  const uniqueLobs = Array.from(new Set(snapshot.reportData.map((r) => r.lineOfBusiness).filter(Boolean))).sort() as string[];
  const uniqueProducts = Array.from(new Set(snapshot.reportData.map((r) => r.product).filter(Boolean))).sort() as string[];
  const uniqueAreas = Array.from(new Set(snapshot.reportData.map((r) => r.area).filter(Boolean))).sort() as string[];
  const uniqueLifecycleStages = Array.from(new Set([
    ...snapshot.reportData.map((r) => r.lifecycleStage?.toLowerCase()),
    ...snapshot.reportData.flatMap((r) => r.childVersions?.map((v: any) => v.lifecycleStage?.toLowerCase()).filter(Boolean) || []),
  ].filter(Boolean))).sort() as string[];

  const { complianceStats, findingsBySeverity } = snapshot;
  const overallRate = Math.min(100, Math.max(0, snapshot.overallComplianceRate));
  const overallTone = overallRate >= 80 ? 'compliant' : overallRate >= 50 ? 'partial' : 'non-compliant';
  const statGroups: Array<{
    title: string;
    pills?: boolean;
    items: Array<{ label: string; value: string | number; tone?: string }>;
  }> = [
    {
      title: 'APIs',
      items: [
        { label: 'Monitored', value: snapshot.totalApisMonitored },
        { label: 'Analyzed', value: snapshot.totalApisAnalyzed },
        { label: 'Not analyzed', value: snapshot.notAnalyzedCount },
      ],
    },
    {
      title: 'Compliance',
      pills: true,
      items: [
        { label: 'Compliant', value: complianceStats.compliant, tone: 'compliant' },
        { label: 'Partial', value: complianceStats.partiallyCompliant, tone: 'partial' },
        { label: 'Non-compliant', value: complianceStats.nonCompliant, tone: 'non-compliant' },
      ],
    },
    {
      title: 'Findings',
      items: [
        { label: 'Total', value: snapshot.totalFindingsCount },
        { label: 'Errors', value: findingsBySeverity.errors, tone: 'non-compliant' },
        { label: 'Warnings', value: findingsBySeverity.warnings, tone: 'partial' },
        { label: 'Info', value: findingsBySeverity.info, tone: 'info' },
        { label: 'Avg / API', value: snapshot.averageFindingsPerApi.toFixed(1) },
      ],
    },
  ];
  const complianceSegments = [
    { label: 'Compliant', value: complianceStats.compliant, tone: 'compliant' },
    { label: 'Partially compliant', value: complianceStats.partiallyCompliant, tone: 'partial' },
    { label: 'Non-compliant', value: complianceStats.nonCompliant, tone: 'non-compliant' },
    { label: 'Not analyzed', value: complianceStats.notAnalyzed, tone: 'muted' },
  ];
  const barTotal = complianceSegments.reduce((sum, s) => sum + s.value, 0) || 1;

  return (
    <div className="snapshot-report-page">
      {/* Header */}
      <div className="snapshot-report-header">
        <div className="header-top">
          <button className="btn-back" onClick={handleBackClick}>
            ← Back to Snapshots
          </button>
          <div className="header-info">
            <h1>Snapshot Report — {formatDate(snapshot.reportDate)}</h1>
            <p className="header-meta">
              {snapshot.period === 'weekly' ? 'Weekly snapshot (7-day period)' : `${snapshot.period} snapshot`}
              {' · '}Generated {new Date(snapshot.generatedAt).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="snapshot-stats">
          <div className="stats-group stats-overall">
            <div className="overall-ring">
              <svg viewBox="0 0 36 36" aria-hidden="true">
                <circle className="ring-track" cx="18" cy="18" r="15.9155" pathLength={100} />
                <circle
                  className={`ring-value tone-${overallTone}`}
                  cx="18"
                  cy="18"
                  r="15.9155"
                  pathLength={100}
                  strokeDasharray={`${overallRate} 100`}
                />
              </svg>
              <span className="ring-label">{Math.round(overallRate)}%</span>
            </div>
            <div>
              <div className="stats-group-title">Overall Compliance</div>
              <div className="overall-value">{snapshot.overallComplianceRate.toFixed(1)}%</div>
            </div>
          </div>
          {statGroups.map((group) => (
            <div className="stats-group" key={group.title}>
              <span className="stats-group-title">{group.title}</span>
              {group.items.map((item) => (
                <span
                  className={`stat-item${group.pills ? ' stat-pill' : ''}${item.tone ? ` tone-${item.tone}` : ''}`}
                  key={item.label}
                >
                  {item.label}
                  <strong>{item.value}</strong>
                </span>
              ))}
            </div>
          ))}
        </div>

        <div className="compliance-bar" role="img" aria-label="Compliance distribution">
          {complianceSegments.map((segment) => (
            <span
              key={segment.label}
              className={`tone-${segment.tone}`}
              style={{ width: `${(segment.value / barTotal) * 100}%` }}
              title={`${segment.label}: ${segment.value}`}
            />
          ))}
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="snapshot-report-toolbar">
        <div className="filter-bar">
          <input
            type="text"
            className="search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search API Name..."
          />
          <button
            className="filter-button"
            onClick={handleOpenFilterModal}
            title="Open filters"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 5.83333H17.5M5.83333 10H14.1667M8.33333 14.1667H11.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {getActiveFilterCount() > 0 && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>

          {/* Active Filter Tags */}
          <div className="active-filters">
            {search && <span className="filter-tag" onClick={() => removeFilter('search')} style={{ cursor: 'pointer' }} title="Remove filter">Search: {search} ✕</span>}
            {filters.lob && <span className="filter-tag" onClick={() => removeFilter('lob')} style={{ cursor: 'pointer' }} title="Remove filter">LOB: {filters.lob} ✕</span>}
            {filters.product && <span className="filter-tag" onClick={() => removeFilter('product')} style={{ cursor: 'pointer' }} title="Remove filter">Product: {filters.product} ✕</span>}
            {filters.area && <span className="filter-tag" onClick={() => removeFilter('area')} style={{ cursor: 'pointer' }} title="Remove filter">Area: {filters.area} ✕</span>}
            {filters.status && <span className="filter-tag" onClick={() => removeFilter('status')} style={{ cursor: 'pointer' }} title="Remove filter">Status: {formatStatus(filters.status)} ✕</span>}
            {filters.enrolled && <span className="filter-tag" onClick={() => removeFilter('enrolled')} style={{ cursor: 'pointer' }} title="Remove filter">Enrolled: {filters.enrolled === 'true' ? 'Yes' : 'No'} ✕</span>}
            {filters.lifecycleStage && <span className="filter-tag" onClick={() => removeFilter('lifecycleStage')} style={{ cursor: 'pointer' }} title="Remove filter">Lifecycle: {filters.lifecycleStage} ✕</span>}
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleExportCsv} disabled={exporting}>
          {exporting ? 'Exporting...' : '⬇ Export to CSV'}
        </button>
      </div>

      {/* Filter Modal */}
      {filterModalOpen && (
        <div className="filter-modal-overlay" onClick={handleCloseFilterModal}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Filter Results</h3>
              <button
                className="close-button"
                onClick={handleCloseFilterModal}
                type="button"
              >
                ✕
              </button>
            </div>
            <div className="filter-modal-body">
              <div className="filter-section">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Line of Business</label>
                    <select
                      value={tempFilters.lob}
                      onChange={(e) => setTempFilters({...tempFilters, lob: e.target.value})}
                    >
                      <option value="">All</option>
                      {uniqueLobs.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Product</label>
                    <select
                      value={tempFilters.product}
                      onChange={(e) => setTempFilters({...tempFilters, product: e.target.value})}
                    >
                      <option value="">All</option>
                      {uniqueProducts.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Area</label>
                    <select
                      value={tempFilters.area}
                      onChange={(e) => setTempFilters({...tempFilters, area: e.target.value})}
                    >
                      <option value="">All</option>
                      {uniqueAreas.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Status</label>
                    <select
                      value={tempFilters.status}
                      onChange={(e) => setTempFilters({...tempFilters, status: e.target.value})}
                    >
                      <option value="">All</option>
                      {uniqueStatuses.map((status) => (
                        <option key={status} value={status}>
                          {formatStatus(status)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Enrolled</label>
                    <select
                      value={tempFilters.enrolled}
                      onChange={(e) => setTempFilters({ ...tempFilters, enrolled: e.target.value as 'true' | 'false' | '' })}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Lifecycle Stage</label>
                    <select
                      value={tempFilters.lifecycleStage}
                      onChange={(e) => setTempFilters({ ...tempFilters, lifecycleStage: e.target.value })}
                    >
                      <option value="">All</option>
                      {uniqueLifecycleStages.map(opt => (
                        <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase()}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="filter-modal-footer">
              <button className="reset-button" onClick={handleResetFiltersModal}>
                Reset
              </button>
              <button className="apply-button" onClick={handleApplyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="snapshot-report-results">
        <div className="results-header">
          <h3>Report Details ({filteredData.length} results)</h3>
          {totalPages > 1 && (
            <span className="results-pagination-info">
              Page {page} of {totalPages}
            </span>
          )}
        </div>

        {/* Table */}
        <div className="table-wrapper">
          {currentPageData.length === 0 ? (
            <div className="no-data">No APIs found matching the selected filters.</div>
          ) : (
            <>
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>API Name</th>
                    <th>Line of Business</th>
                    <th>Product</th>
                    <th>Area</th>
                    <th>Versions</th>
                    <th>Point of Contact</th>
                    <th>Enrolled</th>
                    <th>Lifecycle Stage</th>
                    <th>Status</th>
                    <th>Quality Score</th>
                    <th>Compliance Score</th>
                    <th>Findings</th>
                    <th>Last Analyzed</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((row) => (
                    <React.Fragment key={row.apiId}>
                      <tr>
                        <td>
                          {row.childVersions && row.childVersions.length > 0 ? (
                            <div className="findings-cell">
                              <button
                                className="btn-expand-small"
                                onClick={() => toggleParentExpansion(row.apiId)}
                                title="Toggle versions"
                              >
                                {expandedParents.has(row.apiId) ? '▼' : '▶'}
                              </button>
                              <span>{row.fullApiName}</span>
                            </div>
                          ) : (
                            row.fullApiName
                          )}
                        </td>
                        <td>{row.lineOfBusiness || 'N/A'}</td>
                        <td>{row.product || 'N/A'}</td>
                        <td>{row.area || 'N/A'}</td>
                        <td>{row.apiVersion || 'N/A'}</td>
                        <td>{row.pointOfContact || 'N/A'}</td>
                        <td>
                          <span className={row.isEnrolled ? 'badge badge-compliant' : 'badge badge-not-analyzed'}>
                            {row.isEnrolled ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          {row.lifecycleStage ? (
                            <LifecycleBadge stage={row.lifecycleStage} />
                          ) : (
                            <span style={{ color: '#999' }}>N/A</span>
                          )}
                        </td>
                        <td>
                          {row.childVersions && row.childVersions.length > 0 ? (
                            <span style={{ color: '#666' }}>—</span>
                          ) : (
                            <span className={`badge badge-${row.status || 'unknown'}`}>
                              {row.status === 'compliant'
                                ? 'Compliant'
                                : row.status === 'partially-compliant'
                                ? 'Partially Compliant'
                                : row.status === 'non-compliant'
                                ? 'Non-Compliant'
                                : 'Not Analyzed'}
                            </span>
                          )}
                        </td>
                        <td>{row.childVersions && row.childVersions.length > 0 ? '—' : (row.qualityScore != null ? `${Math.round(row.qualityScore)}%` : 'N/A')}</td>
                        <td>{row.childVersions && row.childVersions.length > 0 ? '—' : (row.complianceScore != null ? `${Math.round(row.complianceScore)}%` : 'N/A')}</td>
                        <td>
                          {row.childVersions && row.childVersions.length > 0 ? (
                            '—'
                          ) : (row.findings && row.findings.length > 0 ? (
                            <div className="findings-cell">
                              <button
                                className="btn-expand-small"
                                onClick={() => toggleFindingsExpansion(row.apiId)}
                                title="Toggle findings"
                              >
                                {expandedFindings.has(row.apiId) ? '▼' : '▶'}
                              </button>
                              <span className="badge badge-info">{row.findings.length}</span>
                            </div>
                          ) : (
                            '0'
                          ))}
                        </td>
                        <td>{row.childVersions && row.childVersions.length > 0 ? '—' : (row.lastAnalyzedAt ? new Date(row.lastAnalyzedAt).toLocaleDateString() : 'N/A')}</td>
                      </tr>

                      {/* Expanded child versions row */}
                      {expandedParents.has(row.apiId) && row.childVersions && row.childVersions.length > 0 && (
                        <tr className="versions-row">
                          <td colSpan={13}>
                            <div className="versions-detail">
                              <table className="versions-table">
                                <thead>
                                  <tr>
                                    <th>Version</th>
                                    <th>Status</th>
                                    <th>Quality Score</th>
                                    <th>Compliance Score</th>
                                    <th>Findings</th>
                                    <th>Last Analyzed</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.childVersions.map((version) => (
                                    <React.Fragment key={version.versionId}>
                                      <tr className="version-detail-row">
                                        <td>{version.versionNumber}</td>
                                        <td>
                                          <span className={`badge badge-${version.status || 'unknown'}`}>
                                            {version.status === 'compliant' ? 'Compliant' :
                                             version.status === 'partially-compliant' ? 'Partially Compliant' :
                                             version.status === 'non-compliant' ? 'Non-Compliant' :
                                             'Not Analyzed'}
                                          </span>
                                        </td>
                                        <td>{version.qualityScore !== undefined ? `${Math.round(version.qualityScore)}%` : 'N/A'}</td>
                                        <td>{version.complianceScore !== undefined ? `${Math.round(version.complianceScore)}%` : 'N/A'}</td>
                                        <td>
                                          {version.findings && version.findings.length > 0 ? (
                                            <div className="findings-cell">
                                              <button
                                                className="btn-expand-small"
                                                onClick={() => toggleFindingsExpansion(version.versionId)}
                                                title="Toggle findings"
                                              >
                                                {expandedFindings.has(version.versionId) ? '▼' : '▶'}
                                              </button>
                                              <span className="badge badge-info">{version.findings.length}</span>
                                            </div>
                                          ) : (
                                            '0'
                                          )}
                                        </td>
                                        <td>{version.lastAnalyzedAt ? new Date(version.lastAnalyzedAt).toLocaleDateString() : 'N/A'}</td>
                                      </tr>

                                      {/* Findings Expansion Row (Third Level) */}
                                      {expandedFindings.has(version.versionId) && version.findings && version.findings.length > 0 && (
                                        <tr className="findings-row">
                                          <td colSpan={6}>
                                            <div className="findings-detail">
                                              <ul className="findings-list">
                                                {version.findings.map((finding, fidx) => (
                                                  <li key={fidx} className={`finding-${finding.severity}`}>
                                                    <span className="finding-icon">
                                                      {finding.severity === 'error' ? '✕' : finding.severity === 'warn' ? '⚠' : 'ℹ'}
                                                    </span>
                                                    <span className="finding-code">{finding.code}</span>
                                                    <span className="finding-message">{finding.message}</span>
                                                    {finding.line && <span className="finding-line">:{finding.line}</span>}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Expanded findings row for standalone APIs */}
                      {expandedFindings.has(row.apiId) && !row.childVersions && row.findings && row.findings.length > 0 && (
                        <tr className="findings-row">
                          <td colSpan={13}>
                            <div className="findings-detail">
                              <ul className="findings-list">
                                {row.findings.map((finding, idx) => (
                                  <li key={idx} className={`finding-${finding.severity}`}>
                                    <span className="finding-icon">
                                      {finding.severity === 'error' ? '✕' : finding.severity === 'warn' ? '⚠' : 'ℹ'}
                                    </span>
                                    <span className="finding-code">{finding.code}</span>
                                    <span className="finding-message">{finding.message}</span>
                                    {finding.line && <span className="finding-line">:{finding.line}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-small"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-small"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnapshotReportPage;
